import { Input, Textarea } from "@chakra-ui/react";
import SharedSelect from "../../../../../../shared/components/Select";
import PatientProfileLookupSelect from "../../../../../entityProfileModal/components/EntityProfileLookupSelect";
import type { PatientProfilePatient } from "../../../../patientProfileModal.types";
import type { PatientInformationDraft, PatientInformationField } from "../patientInformation.types";
import {
  getAgencyMemberLookupOption,
  getSocialWorkerLookupOption,
  getSourceLookupOption,
  hhaGenderOptions,
  isHhaGenderOption,
  kidsInHouseOptions,
  nullableText,
  yesNoOptions,
} from "../patientInformation.utils";

export function InformationInlineEditor(props: {
  draft: PatientInformationDraft;
  patient: PatientProfilePatient;
  field: PatientInformationField;
  onChange: (draft: PatientInformationDraft) => void;
}) {
  const updateField = <TKey extends keyof PatientInformationDraft>(
    key: TKey,
    value: PatientInformationDraft[TKey]
  ) => props.onChange({ ...props.draft, [key]: value });

  if (props.field === "source") {
    return (
      <PatientProfileLookupSelect
        entity="referral-sources"
        label="Select source"
        selectedOption={getSourceLookupOption(props.patient)}
        value={props.draft.source ?? null}
        onChange={(option) =>
          updateField("source", (option?.id ?? null) as PatientInformationDraft["source"])
        }
      />
    );
  }

  if (props.field === "socialWorker") {
    return (
      <PatientProfileLookupSelect
        entity="social-workers"
        label="Select social worker"
        selectedOption={getSocialWorkerLookupOption(props.patient)}
        value={props.draft.socialWorker ?? null}
        onChange={(option) =>
          updateField(
            "socialWorker",
            (option?.id ?? null) as PatientInformationDraft["socialWorker"]
          )
        }
      />
    );
  }

  if (props.field === "assignedIntake") {
    return (
      <PatientProfileLookupSelect
        entity="agency-members"
        label="Select intake"
        query={{ roles: "Intake", status: "Active" }}
        selectedOption={getAgencyMemberLookupOption(props.draft.assignedIntake)}
        value={props.draft.assignedIntake ?? null}
        onChange={(option) =>
          updateField(
            "assignedIntake",
            (option?.id ?? null) as PatientInformationDraft["assignedIntake"]
          )
        }
      />
    );
  }

  if (props.field === "assignedSalesRep") {
    return (
      <PatientProfileLookupSelect
        entity="agency-members"
        label="Select sales rep"
        query={{ roles: "SalesRep", status: "Active" }}
        selectedOption={getAgencyMemberLookupOption(props.draft.assignedSalesRep)}
        value={props.draft.assignedSalesRep ?? null}
        onChange={(option) =>
          updateField(
            "assignedSalesRep",
            (option?.id ?? null) as PatientInformationDraft["assignedSalesRep"]
          )
        }
      />
    );
  }

  if (props.field === "assignedDPS") {
    return (
      <PatientProfileLookupSelect
        entity="agency-members"
        label="Select DPS"
        query={{ roles: "DPS", status: "Active" }}
        selectedOption={getAgencyMemberLookupOption(props.draft.assignedDPS)}
        value={props.draft.assignedDPS ?? null}
        onChange={(option) =>
          updateField("assignedDPS", (option?.id ?? null) as PatientInformationDraft["assignedDPS"])
        }
      />
    );
  }

  if (props.field === "preferredHHAGender") {
    return (
      <SharedSelect
        label="Select gender"
        multiple={false}
        options={hhaGenderOptions}
        size="sm"
        value={
          isHhaGenderOption(props.draft.preferredHHAGender) ? props.draft.preferredHHAGender : null
        }
        onChange={(value) => updateField("preferredHHAGender", value ?? null)}
      />
    );
  }

  if (props.field === "kidsInHouse") {
    return (
      <SharedSelect
        label="Select kids"
        multiple={false}
        options={kidsInHouseOptions}
        size="sm"
        value={props.draft.kidsInHouse}
        onChange={(value) => updateField("kidsInHouse", value ?? null)}
      />
    );
  }

  if (props.field === "smoking" || props.field === "kosherHome") {
    return (
      <SharedSelect
        label="Select value"
        multiple={false}
        options={yesNoOptions}
        size="sm"
        value={props.draft[props.field] ?? null}
        onChange={(value) => updateField(props.field, value ?? null)}
      />
    );
  }

  if (props.field === "wageParityNumber") {
    return (
      <Input
        autoFocus
        size="sm"
        type="number"
        value={props.draft.wageParityNumber ?? ""}
        onChange={(event) =>
          updateField(
            "wageParityNumber",
            event.target.value === "" ? null : Number(event.target.value)
          )
        }
      />
    );
  }

  if (props.field === "generalNotes") {
    return (
      <Textarea
        autoFocus
        rows={4}
        size="sm"
        value={props.draft.generalNotes ?? ""}
        onChange={(event) => updateField("generalNotes", nullableText(event.target.value))}
      />
    );
  }

  return (
    <Input
      autoFocus
      size="sm"
      value={String(props.draft[props.field] ?? "")}
      onChange={(event) => updateField(props.field, nullableText(event.target.value) as never)}
    />
  );
}
